import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { configManager } from 'Helpers/config-manager';
import { securityClient } from 'securityClient';
import { setExpired } from 'State/subscriptionSlice';

const parseJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')));
  } catch {
    return null;
  }
};

const REFRESH_THRESHOLD_SECONDS = 300; // 5 minutes

const isTokenExpiringSoon = (token: string): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  return payload.exp - Math.floor(Date.now() / 1000) < REFRESH_THRESHOLD_SECONDS;
};

// Module-level promise acts as a mutex — concurrent 401s all await the same refresh
let refreshPromise: Promise<string | null> | null = null;

const attemptRefresh = async (baseUrl: string): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) return null;

      const storedIdToken = localStorage.getItem('idToken');
      const userId = storedIdToken ? parseJwtPayload(storedIdToken)?.sub ?? null : null;
      if (!userId) return null;

      const response = await fetch(`${baseUrl}auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: storedRefreshToken, userId }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      localStorage.setItem('idToken', data.idToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      return data.idToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const config = configManager.getConfig();
  const baseUrl = `${config?.api.httpProtocol}://${config?.api.url}/api/v1/`;

  const fetchQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers, { endpoint }) => {
      const PUBLIC_ENDPOINTS = ['signup', 'confirmSignup', 'signin', 'googleAuth', 'forgotPassword', 'confirmForgotPassword'];
      const ACCESS_TOKEN_ENDPOINTS = ['changePassword'];
      if (!PUBLIC_ENDPOINTS.includes(endpoint)) {
        if (ACCESS_TOKEN_ENDPOINTS.includes(endpoint)) {
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          let access_token = await securityClient.getAccessTokenSilently();
          if (access_token && isTokenExpiringSoon(access_token)) {
            const refreshed = await attemptRefresh(baseUrl);
            if (refreshed) access_token = refreshed;
          }
          if (access_token) headers.set('Authorization', `Bearer ${access_token}`);
        }
      }
      return headers;
    },
  });

  let result = await fetchQuery(args, api, extraOptions);

  const PUBLIC_PATHS = ['auth/signin', 'auth/signup', 'auth/google', 'auth/forgot-password', 'auth/confirm-forgot-password', 'auth/confirm-signup', 'auth/resend-confirmation', 'auth/change-password'];
  const requestUrl = typeof args === 'string' ? args : (args as FetchArgs).url;
  const isPublicRequest = PUBLIC_PATHS.some(path => requestUrl.includes(path));

  if (result.error?.status === 401 && !isPublicRequest) {
    const newToken = await attemptRefresh(baseUrl);
    if (newToken) {
      result = await fetchQuery(args, api, extraOptions);
    } else {
      localStorage.clear();
      // '/login', not '/': '/' is the public marketing splash whose only CTA is sign-up,
      // which is the wrong destination for a user whose session just expired. This also
      // has to agree with ProtectedLayout's redirect — a hard navigation here overrides
      // the router, so a mismatch means the landing page depends on which one wins.
      window.location.href = '/login';
    }
  }

  if (result.error?.status === 403) {
    const data = result.error.data as any;
    if (data?.error === 'TENANT_EXPIRED') {
      api.dispatch(setExpired(true));
    }
  }
  // Not every endpoint is gated by tenant-expired status (e.g. company/subscription must
  // stay reachable for expired tenants so they can view/renew billing). So a successful
  // response from one endpoint doesn't prove another is unblocked — isExpired is only
  // cleared by an explicit ExpiredModal dismiss or a confirmed post-checkout check
  // (see Pages/Subscribe/Success.tsx), never by an unrelated request merely succeeding.

  return result;
};

export const api = createApi({
  baseQuery,
  tagTypes: ['Integrations', 'Deals', 'Sync', 'Deal', 'Pipelines', 'Owners', 'EmailContent', 'DealInsights', 'ContactInsights', 'RevOpsBrief', 'RevOpsBriefFeedback', 'Subscription', 'Conversations', 'HubspotOwnerLink'],
  endpoints: () => ({}),
});
